import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("packo_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("packo_token");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setUser(undefined);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const applyAuth = (data) => {
    localStorage.setItem("packo_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return applyAuth(data);
    } catch (e) {
      throw new Error(apiError(e, "Login failed"));
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      return applyAuth(data);
    } catch (e) {
      throw new Error(apiError(e, "Registration failed"));
    }
  };

  const googleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    return applyAuth(data);
  };

  const logout = () => {
    localStorage.removeItem("packo_token");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* keep current state */ }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, googleSession, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
